import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

// One checkout gate covers both the drawer and bag, including replaced sections.
const pendingCartUpdates = new Set();
let cartNeedsReview = false;
const checkoutOriginalState = new WeakMap();
function syncCheckoutGate() {
  const locked = pendingCartUpdates.size > 0 || cartNeedsReview;
  document.querySelectorAll('[data-bw-checkout-entry]').forEach(node => {
    if (!checkoutOriginalState.has(node)) checkoutOriginalState.set(node, {disabled:node.disabled, inert:node.inert});
    const original = checkoutOriginalState.get(node);
    if (node instanceof HTMLButtonElement) node.disabled = locked || original.disabled;
    node.inert = locked || !!original.inert;
    if (locked) node.setAttribute('aria-disabled','true'); else node.removeAttribute('aria-disabled');
  });
  document.querySelectorAll('[data-bw-checkout-status]').forEach(node => {
    node.hidden = !locked;
    const state = cartNeedsReview ? 'review' : pendingCartUpdates.size ? 'pending' : '';
    if (node.dataset.state === state) return;
    node.dataset.state = state;
    if (cartNeedsReview) {
      const link = document.createElement('a'); link.href = Theme.routes.cart_url; link.textContent = 'Check your bag';
      node.replaceChildren('We could not confirm your bag update. ', link, ' before checking out.');
    } else node.textContent = state ? 'Updating your bag. Please wait before checking out.' : '';
  });
}
function preventPendingCheckout(event) {
  if (!pendingCartUpdates.size && !cartNeedsReview) return;
  if (event.type === 'keydown' && !['Enter',' '].includes(event.key)) return;
  const path = event.composedPath();
  const entry = path.find(node => node instanceof Element && node.matches('[data-bw-checkout-entry]'));
  const cartSubmit = event.type === 'submit' && event.target instanceof Element && event.target.matches('form.cart-form');
  if (!entry && !cartSubmit) return;
  event.preventDefault(); event.stopImmediatePropagation();
  document.querySelector('[data-bw-checkout-status]:not([hidden])')?.focus({preventScroll:true});
}
for (const name of ['click','keydown','submit']) document.addEventListener(name, preventPendingCheckout, true);
new MutationObserver(syncCheckoutGate).observe(document.documentElement, {childList:true, subtree:true});
window.addEventListener('pageshow', syncCheckoutGate);
document.addEventListener('product-form:busy', event => {
  if (event.detail.busy) pendingCartUpdates.add(event.target); else pendingCartUpdates.delete(event.target);
  syncCheckoutGate();
}, true);
document.addEventListener('product-form:unconfirmed', () => {cartNeedsReview = true; syncCheckoutGate();}, true);

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    if (this.updateQuantity({
      line,
      quantity,
      action: 'change',
    }) === false) return;
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.dataset.updating === 'true') return false;
    if (cartNeedsReview) {
      this.querySelectorAll('input[name="updates[]"]').forEach(input=>input.value=input.defaultValue);
      syncCheckoutGate(); return false;
    }
    this.dataset.updating = 'true';
    this.setAttribute('aria-busy','true');
    pendingCartUpdates.add(this); syncCheckoutGate();
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const confirmed = [...this.querySelectorAll('input[name="updates[]"],quantity-selector-component input')].map(input=>[input,input.defaultValue]);
    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        if (!parsedResponseText.sections || typeof parsedResponseText.sections[this.sectionId] !== 'string') throw new Error('Cart response did not contain the updated section');
        this.querySelector('[data-cart-update-error]')?.remove();
        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        cartNeedsReview = true; syncCheckoutGate();
        confirmed.forEach(([input,value])=>{if(input.isConnected)input.value=value;});
        let feedback=this.querySelector('[data-cart-update-error]');
        if(!feedback){feedback=document.createElement('p');feedback.dataset.cartUpdateError='';feedback.setAttribute('role','alert');feedback.setAttribute('aria-atomic','true');this.prepend(feedback);}
        const link=document.createElement('a');link.href=Theme.routes.cart_url;link.textContent='Check your bag';link.style.textDecoration='underline';
        feedback.replaceChildren('We could not confirm your bag update. ',link,' before trying again.');
        console.error(error);
      })
      .finally(() => {
        resetShimmer(this);
        pendingCartUpdates.delete(this); syncCheckoutGate();
        delete this.dataset.updating;
        this.removeAttribute('aria-busy');
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
